import React from "react";
import { Alert, Steps } from "antd";

import LoadingModal from "../../../../../commons/LoadingModal";
import DescriptionText from "../../../../../../CommonComponents/DescriptionText/DescriptionText";

import useRegistrationForm from "./useRegistrationForm";
import GeneralSetupStep from "./GeneralSetupStep";
import FormFieldsStep from "./FormFieldsStep";
import DonationStep from "./DonationStep";
import CompletionStep from "./CompletionStep";
import EditChapterModal from "./EditChapterModal";

import "./RegistrationForm.css";

const RegistrationForm = () => {
  const form = useRegistrationForm();

  const {
    loading,
    isTransitioning,
    errorInForm,
    basicsStep,
    formFieldStep,
    donationStep,
    previewStep,
    groupName,
    backgroundColor,
    handleErrorMessageClose,
    showChapterModal,
    chapters,
    includeNone,
    setChapters,
    setIncludeNone,
    setShowChapterModal,
  } = form;

  return (
    <div style={{ paddingTop: 2 }}>
      <LoadingModal
        title="Loading Document"
        message={!isTransitioning && "Fetching data"}
        loading={loading}
      />

      <DescriptionText showBackground>
        <p className="pb-2">
          Set up the registration flow including member benefits, registration
          form fields and donations.
        </p>
        <p>
          Stripe should be set to send members a &apos;reminder&apos; email
          before auto-renewal and for expiring payment details. Note: Some
          groups disable the &apos;reminder&apos; as all member packages renew
          on the &apos;package expiration date&apos;, not the members purchase
          anniversary. (
          <a
            href="https://dashboard.stripe.com/settings/billing/automatic"
            target="_blank"
            rel="noopener noreferrer"
          >
            Stripe email setup
          </a>
          )
        </p>
      </DescriptionText>

      {showChapterModal > 0 && (
        <EditChapterModal
          data={{ chapters, includeNone }}
          showModal={showChapterModal}
          onUpdateField={(data) => {
            if (data.chapters !== undefined) setChapters(data.chapters);
            if (data.includeNone !== undefined) setIncludeNone(data.includeNone);
            setShowChapterModal("");
          }}
          onClose={() => setShowChapterModal("")}
        />
      )}

      {errorInForm.isError && errorInForm.visible && (
        <div style={{ padding: "8px 24px" }}>
          <Alert
            message="Error"
            description={errorInForm.message}
            type="error"
            closable
            afterClose={handleErrorMessageClose}
          />
        </div>
      )}

      <div className="reg-form">
        <div className="reg-form-steps">
          <Steps>
            <Steps.Step status={basicsStep} title="General Setup" />
            <Steps.Step status={formFieldStep} title="Form Fields" />
            <Steps.Step status={donationStep} title="Donations" />
            <Steps.Step status={previewStep} title="Complete" />
          </Steps>
        </div>

        {basicsStep === "process" && (
          <GeneralSetupStep
            groupName={groupName}
            title={form.title}
            intro={form.intro}
            benefits={form.benefits}
            groupImageList={form.groupImageList}
            appScreenshotListFiltered={form.appScreenshotListFiltered}
            groupTermsURL={form.groupTermsURL}
            thankYouNote={form.thankYouNote}
            supportEmail={form.supportEmail}
            processingFee={form.processingFee}
            requireMembershipPackage={form.requireMembershipPackage}
            joinSVG={form.joinSVG}
            stripeSetupConfirmation={form.stripeSetupConfirmation}
            showAddressVerification={form.showAddressVerification}
            addressVerification={form.addressVerification}
            addressVerificationTriggered={form.addressVerificationTriggered}
            highlightPackageRequirement={form.highlightPackageRequirement}
            backgroundColor={backgroundColor}
            requireMembershipPackageRef={form.requireMembershipPackageRef}
            handleInputChange={form.handleInputChange}
            handleEditorStateChange={form.handleEditorStateChange}
            handleImageChange={form.handleImageChange}
            handleStripeSetupConfirmation={form.handleStripeSetupConfirmation}
            uploadAction={form.uploadAction}
            changeSelect={form.changeSelect}
            handleNext={form.handleNext}
            setRequireMembershipPackage={form.setRequireMembershipPackage}
            setAddressVerification={form.setAddressVerification}
            setAddressVerificationTriggered={form.setAddressVerificationTriggered}
          />
        )}

        {formFieldStep === "process" && (
          <FormFieldsStep
            formFields={form.formFields}
            customFieldDetails={form.customFieldDetails}
            chapters={form.chapters}
            backgroundColor={backgroundColor}
            handleCheckChange={form.handleCheckChange}
            handleFormFieldSelectChange={form.handleFormFieldSelectChange}
            handleCustomFieldChange={form.handleCustomFieldChange}
            handleNext={form.handleNext}
            setShowChapterModal={form.setShowChapterModal}
          />
        )}

        {donationStep === "process" && (
          <DonationStep
            partnerId={form.partnerId}
            allPartners={form.allPartners}
            donationAutorenewal={form.donationAutorenewal}
            backgroundColor={backgroundColor}
            handlePartnerChange={form.handlePartnerChange}
            handleNext={form.handleNext}
            setDonationAutorenewal={form.setDonationAutorenewal}
          />
        )}

        {previewStep === "process" && (
          <CompletionStep
            groupName={groupName}
            joinSVG={form.joinSVG}
            backgroundColor={backgroundColor}
            handleNext={form.handleNext}
          />
        )}
      </div>
    </div>
  );
};

export default RegistrationForm;
